<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { reportFilters } from '@/modules/reportTemplates/filters'

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTheme } from 'vuetify'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'

import DateRangeFilterCard from '@/components/createdUi/DateRangeFilterCard.vue'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { ERouteNames } from '@/router/ERouteNames'
import { formatDateOnly, formatTimeOnly, getPersonDisplayName } from '@/utils/formatters'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useCitizenInquiriesStore } from '../stores/citizenInquiriesStore'
import { APPEAL_STATUS_LABELS, APPEAL_TOPIC_LABELS, type IAppealListItem } from '../types/IAppeal'
import CitizenInquiryQrModal from './CitizenInquiryQrModal.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { APPEALS_ANCHORS, APPEALS_CONTEXTS } from '@/modules/onboarding/scenarios/appeals/anchors'
import { APPEALS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/appeals/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

const theme = useTheme()
const router = useRouter()
const { smAndDown, xs } = useDisplay()
const feedbackStore = useFeedbackStore()
const permissionsStore = usePermissionsStore()
const store = useCitizenInquiriesStore()

const { currentSortString, nextCursor } = storeToRefs(store)

/* Управление пагинацией с сохранением в localStorage */
const { itemsPerPage } = useTableItemsPerPage('citizen_inquiries', 25)
const { hideCompleted } = useTableHideCompleted('citizen_inquiries')
store.filters.is_actual = hideCompleted.value ? true : null
store.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  store.itemsPerPage = newVal
})

/* Модалка QR-кода */
const showQrModal = ref(false)
const canCreateAppeal = computed(() => Boolean(permissionsStore.isSuperuser || permissionsStore.modules.appeals?.create))
const canEditChannels = computed(() => Boolean(permissionsStore.isSuperuser || permissionsStore.modules.appeals?.channel_edit))
const canEditHospitalSettings = computed(() => Boolean(permissionsStore.isSuperuser || permissionsStore.modules.hospital?.settings_edit))
const isModuleLocked = computed(() => Boolean(store.moduleStatus?.is_locked))

// Что именно мешает открыть модуль: согласие собирается из реквизитов организации, соглашение организация загружает сама
const lockChecklist = computed(() => {
  const status = store.moduleStatus

  return [
    {
      title: 'Согласие на обработку персональных данных',
      done: Boolean(status?.has_consent && status?.is_consent_synced),
      hint: status?.has_consent
        ? 'Сформировано, но не синхронизировано с личным кабинетом пациента.'
        : 'Формируется автоматически, когда в настройках организации заполнены полное наименование, ИНН, ОГРН, юридический адрес и электронная почта.',
    },
    {
      title: 'Соглашение об обработке персональных данных',
      done: Boolean(status?.has_agreement && status?.is_synced),
      hint: status?.has_agreement
        ? 'Загружено, но не синхронизировано с личным кабинетом пациента.'
        : 'PDF-документ организации, загружается в настройках организации.',
    },
  ]
})

const openQrModal = async () => {
  try {
    await store.fetchQrCode()
    showQrModal.value = true
  } catch (error) {
    // Ошибка отображается тостом внутри fetchQrCode
  }
}

const openCreateModal = async () => {
  await router.push({
    name: ERouteNames.CITIZEN_INQUIRY_CREATE,
  })
}

const goToOrganizationSettings = async () => {
  await router.push({
    name: ERouteNames.PORTAL_SETTINGS,
  })
}

/* Обработчик применения фильтра по номеру */
const applyNumberFilterWrapper = () => {
  const inputStr = tableLogic.tempValues.value.numbers || ''
  if (inputStr.trim() === '') {
    store.filters.numbers = []
  } else {
    store.filters.numbers = inputStr
      .split(/[,;\s]+/)
      .map((num: string) => Number.parseInt(num.trim()))
      .filter((num: number) => !Number.isNaN(num) && num > 0)
  }
  tableLogic.filterMenus.value.numbers = false
  tableLogic.triggerServerFilter()
}

/* Конфигурация полей фильтрации для useTableFilters */
const filterConfigs: IFilterConfig[] = [
  {
    key: 'numbers',
    labelPrefix: 'Номер: ',
    type: 'number',
  },
  {
    key: 'topic',
    labelPrefix: 'Тема: ',
    type: 'tree',
    getCustomLabel: (val: string) => APPEAL_TOPIC_LABELS[val] || val,
  },
  {
    key: 'channels',
    labelPrefix: 'Канал: ',
    type: 'tree',
  },
  {
    key: 'status',
    labelPrefix: 'Статус: ',
    type: 'status',
    getCustomLabel: (val: string) => APPEAL_STATUS_LABELS[val] || val,
  },
  {
    key: 'date_time',
    labelPrefix: 'Дата: ',
    type: 'date_range',
    rangeStartKey: 'created_from',
    rangeEndKey: 'created_to',
  },
  {
    key: 'is_actual',
    labelPrefix: '',
    type: 'boolean',
    silent: true,
  },
]

/* Инициализация логики таблицы через composable useTableFilters */
const tableLogic = useTableFilters({
  filters: computed(() => store.filters),
  searchChoices: computed(() => store.searchChoices) as any,
  filterChoices: computed(() => store.filterChoices) as any,
  filterLoading: store.filterLoading,
  fetchFilterChoices: (key: string) => store.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number) => store.fetchAppeals(cursor, limit),
  currentSortString,
  itemsPerPage,
  resetFilters: () => {
    store.resetFilters()
  },
  nextCursor,
  loadedItemsCount: computed(() => store.items.length),
  totalItemsCount: computed(() => store.totalCount),
  sortMap: {
    number: 'number',
    created_at: 'created_at',
    topic: 'topic',
    status: 'status',
  },
  filterConfigs,
})

const isHideAnswered = computed({
  get: () => store.filters.is_actual === true,
  set: (val: boolean) => {
    store.filters.is_actual = val ? true : null
    hideCompleted.value = val
    tableLogic.triggerServerFilter()
  },
})

/* Заголовки таблицы */
const headers = computed(() => [
  { title: '№', key: 'number', maxWidth: '4.5rem', sortable: true, filterType: 'number', filterKey: 'numbers' },
  { title: 'Дата', key: 'created_at', sortable: true, filterType: 'date_range', filterKey: 'date_time' },
  { title: 'Канал', key: 'channel', sortable: false, filterType: 'tree', filterKey: 'channels' },
  { title: 'Тема', key: 'topic', sortable: true, filterType: 'tree', filterKey: 'topic' },
  { title: 'Заявитель', key: 'name', sortable: false },
  { title: 'Контакты', key: 'contacts', sortable: false },
  { title: 'Текст обращения', key: 'text', sortable: false },
  { title: 'Ответ', key: 'reply_text', sortable: false },
  { title: 'Координатор', key: 'coordinator', sortable: false },
  { title: 'Ответил', key: 'replied_by', sortable: false },
  { title: 'Дата ответа', key: 'replied_at', sortable: false },
  { title: 'Статус', key: 'status', sortable: true, filterType: 'tree', filterKey: 'status' },
] as any[])

const displayedItems = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return store.items.slice(start, end)
})

/* Онбординг: демо-обращения первыми в таблице и действия, которыми сценарий открывает меню, настройки и форму */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('appeal', displayedItems)
const onboardingFormDemoId = computed(() => onboardingStore.activeDemoItems[APPEALS_FORM_DEMO_INDEX]?.list.id ?? null)
const isActionsMenuOpen = ref<number | undefined>(undefined)

useOnboardingContext({
  names: [APPEALS_CONTEXTS.table],
  scenarioId: 'appeals',
  root: () => document.body,
  actions: {
    openActionsMenu: () => {
      if (smAndDown.value)
        isActionsMenuOpen.value = 0
    },
    openSettings: async () => { await router.push({ name: ERouteNames.CITIZEN_INQUIRIES_SETTINGS }) },
    openDemoForm: async () => {
      if (onboardingFormDemoId.value !== null)
        await router.push({ name: ERouteNames.CITIZEN_INQUIRY_DETAIL, params: { id: String(onboardingFormDemoId.value) }, query: { [ONBOARDING_DEMO_QUERY]: '1' } })
    },
    closeForm: async () => { await router.push({ name: ERouteNames.CITIZEN_INQUIRIES }) },
  },
})

onMounted(async () => {
  const status = await store.fetchModuleStatus()
  if (status?.is_locked) return
  await store.fetchAppeals(undefined, itemsPerPage.value)

  // Автостарт только на самом реестре: при прямом заходе в карточку обращения обучение не перебивает работу
  if (router.currentRoute.value.name === ERouteNames.CITIZEN_INQUIRIES)
    void onboardingStore.autoStart('appeals')
})

const handleRowClick = async (item: any) => {
  const target = item?.item || item?.raw || item
  const id = target?.id || target?.raw?.id
  if (!id) return
  await router.push({
    name: ERouteNames.CITIZEN_INQUIRY_DETAIL,
    params: { id: String(id) },
  })
}

const rowProps = (item: any) => {
  const row = item?.item || item

  return {
    onClick: () => handleRowClick(row),
    'data-onboarding': row?.id === onboardingFormDemoId.value ? APPEALS_ANCHORS.table.demoRow : undefined,
  }
}

const applyDateFilterWrapper = () => {
  tableLogic.applyDateFilter('date_time')
}

const getApplicantName = (item: IAppealListItem) => {
  return item.name || item.full_name || item.patient_name || item.author || ''
}

const getRepliedByName = (item: IAppealListItem) => {
  if (!item.replied_by) return ''
  return getPersonDisplayName(item.replied_by as any)
}

const getCoordinatorName = (item: IAppealListItem) => {
  if (!item.coordinator) return ''
  return getPersonDisplayName(item.coordinator as any)
}

const templateFilters = computed(() => reportFilters(store.filters))
</script>

<template>
  <!-- Шапка страницы по эталону TicketsTable.vue / IncidentsTable.vue -->
  <header
    v-if="!isModuleLocked"
    :class="{ 'd-flex flex-column gap-2 mb-4': smAndDown }"
  >
    <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
      <VBtn
        v-if="canCreateAppeal"
        :class="{ 'w-100': smAndDown }"
        variant="elevated"
        :loading="feedbackStore.isGlobalLoading"
        prepend-icon="ri-add-line"
        class="general-button"
        :data-onboarding="APPEALS_ANCHORS.table.create"
        @click="openCreateModal"
      >
        Создать обращение
      </VBtn>
      <div v-else />

      <!-- На мобильных скрываем кнопки действий в Expansion Panel -->
      <VExpansionPanels v-if="smAndDown" v-model="isActionsMenuOpen" class="w-100 mt-1">
        <VExpansionPanel>
          <VExpansionPanelTitle class="d-flex align-center py-2 px-4" style="min-height: 48px;">
            <div class="d-flex align-center gap-2">
              <VIcon icon="ri-menu-2-line" size="20" />
              <span class="text-subtitle-1 font-weight-medium">Меню действий</span>
            </div>
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <div class="d-flex flex-column gap-2 w-100">
              <VBtn
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-qr-code-line"
                :loading="store.isQrLoading"
                :data-onboarding="APPEALS_ANCHORS.table.qr"
                @click="openQrModal"
              >
                QR-код обращений
              </VBtn>

              <!-- Переход на страницу настроек модуля (мобильная версия) -->
              <VBtn
                v-if="canEditChannels"
                class="w-100"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-settings-4-line"
                :data-onboarding="APPEALS_ANCHORS.table.settings"
                @click="router.push({ name: ERouteNames.CITIZEN_INQUIRIES_SETTINGS })"
              >
                Настройки
              </VBtn>

              <div class="d-flex justify-end gap-2">
                <ReportTemplateMenu object-type="appeal" context="collection" :filters="templateFilters" :sort="store.currentSortString" variant="toolbar" />
                <OnboardingLaunchButton scenario="appeals" />
              </div>
            </div>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <!-- На ПК показываем обычный горизонтальный ряд кнопок -->
      <div
        v-else
        class="d-flex align-center gap-2"
      >
        <VBtn
          variant="outlined"
          color="secondary"
          prepend-icon="ri-qr-code-line"
          :loading="store.isQrLoading"
          :data-onboarding="APPEALS_ANCHORS.table.qr"
          @click="openQrModal"
        >
          QR-код обращений
        </VBtn>

        <!-- Переход на страницу настроек модуля (десктоп версия) -->
        <VBtn
          v-if="canEditChannels"
          variant="outlined"
          color="secondary"
          class="bg-surface"
          prepend-icon="ri-settings-4-line"
          :data-onboarding="APPEALS_ANCHORS.table.settings"
          @click="router.push({ name: ERouteNames.CITIZEN_INQUIRIES_SETTINGS })"
        >
          Настройки
        </VBtn>

        <ReportTemplateMenu object-type="appeal" context="collection" :filters="templateFilters" :sort="store.currentSortString" variant="toolbar" />
        <OnboardingLaunchButton scenario="appeals" />
      </div>
    </div>
  </header>

  <div class="wrapper">
    <!-- Заглушка блокировки модуля: нужны оба документа ПД, синхронизированные с ЛК пациента -->
    <VCard
      v-if="isModuleLocked"
      class="pa-8 d-flex flex-column align-center justify-center text-center gap-4"
    >
      <VIcon
        icon="ri-lock-2-line"
        size="48"
        color="warning"
      />
      <div class="d-flex flex-column gap-1">
        <span class="text-h5 font-weight-semibold">Модуль обращений граждан заблокирован</span>
        <span class="text-body-1 text-medium-emphasis">
          <template v-if="canEditHospitalSettings">
            Модуль заработает, когда в настройках организации будут готовы оба документа и синхронизированы с личным кабинетом пациента.
          </template>
          <template v-else>
            Для разблокировки модуля обратитесь к администратору портала: в настройках организации должны быть готовы оба документа об обработке персональных данных.
          </template>
        </span>
      </div>

      <div class="d-flex flex-column gap-2 text-start">
        <div
          v-for="item in lockChecklist"
          :key="item.title"
          class="d-flex align-start gap-2"
        >
          <VIcon
            :icon="item.done ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'"
            :color="item.done ? 'success' : 'error'"
            size="20"
          />
          <div class="d-flex flex-column">
            <span class="text-body-1">{{ item.title }}</span>
            <span v-if="!item.done" class="text-body-2 text-medium-emphasis">{{ item.hint }}</span>
          </div>
        </div>
      </div>

      <div class="d-flex align-center gap-3 flex-wrap justify-center">
        <VBtn
          v-if="canEditHospitalSettings"
          color="primary"
          prepend-icon="ri-settings-4-line"
          @click="goToOrganizationSettings"
        >
          Перейти в настройки организации
        </VBtn>
      </div>
    </VCard>

    <div v-else>
      <div
        class="d-flex align-center gap-4"
        :class="[
          tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
          { 'flex-column': smAndDown },
        ]"
      >
        <div
          class="d-flex align-center gap-2 w-100"
          :class="{ 'flex-column': smAndDown }"
        >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0"
            :class="smAndDown ? 'w-100' : 'w-25'"
            :data-onboarding="APPEALS_ANCHORS.table.search"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="tableLogic.applySearchFilter()"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            :class="{ 'w-100': smAndDown }"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="tableLogic.applySearchFilter()"
          />

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            :class="[
              { 'w-100': smAndDown },
              !smAndDown && 'ml-auto',
            ]"
            height="48"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :data-onboarding="APPEALS_ANCHORS.table.resetFilters"
            :loading="store.isLoading"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>

          <VSwitch
            v-model="isHideAnswered"
            :data-onboarding="APPEALS_ANCHORS.table.hideAnswered"
            label="Скрыть отвеченные"
            density="compact"
            color="primary"
            hide-details
            class="flex-grow-0"
            :class="{ 'ml-auto': !smAndDown && !tableLogic.hasActiveFilters.value }"
          />
        </div>
      </div>

      <!-- Панель активных чипов фильтров -->
      <div
        v-if="tableLogic.activeFilterChips.value.length"
        class="filter-chip d-flex ml-4 align-center gap-2 py-3"
      >
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="`${chip.type}-${chip.value || 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <!-- Таблица обращений -->
      <VDataTableServer
        :items="tableRows"
        :items-length="store.totalCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
        @click:row="(_e: any, row: any) => handleRowClick(row?.item || row)"
      >
        <template #no-data>
          <div class="text-center text-italic py-4 text-medium-emphasis">
            Обращения не найдены
          </div>
        </template>

        <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>

            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <!-- Спиннер -->
            <div
              v-if="tableLogic.filterLoading?.[col.filterKey!]"
              class="d-flex align-center justify-center ml-1"
              style="width: 28px; height: 28px"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <!-- Иконки/Меню -->
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :data-onboarding="APPEALS_ANCHORS.table.filterPrefix + col.key"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <!-- Number Filter Modal -->
            <VMenu
              v-if="col.filterType === 'number'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="pa-4">
                <VTextField
                  v-model="tableLogic.tempValues.value[col.filterKey!]"
                  autofocus
                  placeholder="Например: 1, 2, 3"
                  hide-details
                  width="220px"
                  @keyup.enter="applyNumberFilterWrapper"
                />
                <div class="d-flex justify-end mt-2">
                  <VBtn
                    :class="{ 'w-100': smAndDown }"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    @click="applyNumberFilterWrapper"
                  >
                    Применить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>

            <!-- Date Filter -->
            <VMenu
              v-else-if="col.filterType === 'date_range'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <DateRangeFilterCard
                :date-after="tableLogic.tempValues.value.created_from || ''"
                :date-before="tableLogic.tempValues.value.created_to || ''"
                @update:date-after="tableLogic.tempValues.value.created_from = $event"
                @update:date-before="tableLogic.tempValues.value.created_to = $event"
                @apply="applyDateFilterWrapper"
              />
            </VMenu>

            <!-- Tree Filter (Topic, Status) -->
            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="store"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>
        <!-- КОНЕЦ ДИНАМИЧЕСКИХ ЗАГОЛОВКОВ -->

        <!-- Колонка: Номер -->
        <template #[`item.number`]="{ item }">
          <span>{{ item.number ?? '' }}</span>
        </template>

        <!-- Колонка: Дата -->
        <template #[`item.created_at`]="{ item }">
          <div class="d-flex flex-column">
            <span>{{ formatDateOnly(item.created_at) }}</span>
            <span>{{ formatTimeOnly(item.created_at) }}</span>
          </div>
        </template>

        <!-- Колонка: Канал поступления -->
        <template #[`item.channel`]="{ item }">
          <span>{{ item.channel?.name || '' }}</span>
        </template>

        <!-- Колонка: Тема (текст) -->
        <template #[`item.topic`]="{ item }">
          <span>{{ APPEAL_TOPIC_LABELS[item.topic] || item.topic || '' }}</span>
        </template>

        <!-- Колонка: Заявитель -->
        <template #[`item.name`]="{ item }">
          <span>{{ getApplicantName(item) }}</span>
        </template>

        <!-- Колонка: Контакты -->
        <template #[`item.contacts`]="{ item }">
          <div v-if="item.email || item.phone" class="d-flex flex-column">
            <span v-if="item.email">{{ item.email }}</span>
            <span v-if="item.phone">{{ item.phone }}</span>
          </div>
        </template>

        <!-- Колонка: Текст обращения -->
        <template #[`item.text`]="{ item }">
          <div v-if="item.text" class="py-2">
            <CollapsibleText
              :text="item.text"
              :max-length="120"
            />
          </div>
        </template>

        <!-- Колонка: Текст ответа -->
        <template #[`item.reply_text`]="{ item }">
          <div v-if="item.reply_text" class="py-2">
            <CollapsibleText
              :text="item.reply_text"
              :max-length="120"
            />
          </div>
        </template>

        <!-- Колонка: Координатор -->
        <template #[`item.coordinator`]="{ item }">
          <span v-if="getCoordinatorName(item)">{{ getCoordinatorName(item) }}</span>
        </template>

        <!-- Колонка: Ответил -->
        <template #[`item.replied_by`]="{ item }">
          <span v-if="getRepliedByName(item)">{{ getRepliedByName(item) }}</span>
        </template>

        <!-- Колонка: Дата ответа -->
        <template #[`item.replied_at`]="{ item }">
          <div v-if="item.replied_at" class="d-flex flex-column">
            <span>{{ formatDateOnly(item.replied_at) }}</span>
            <span>{{ formatTimeOnly(item.replied_at) }}</span>
          </div>
        </template>

        <!-- Колонка: Статус -->
        <template #[`item.status`]="{ item }">
          <VChip
            v-if="item.status"
            class="status-badge"
            :class="item.status"
            size="small"
          >
            {{ APPEAL_STATUS_LABELS[item.status] || item.status }}
          </VChip>
        </template>

        <!-- Футер пагинации по эталонному паттерну проекта -->
        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>

  <!-- Модалка QR-кода -->
  <CitizenInquiryQrModal
    v-if="showQrModal"
    v-model="showQrModal"
  />

  <!-- Дочерний маршрут для модального окна просмотра/ответа -->
  <RouterView />
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}

.text-italic {
  font-style: italic;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

/* Компактные служебные колонки */
:deep(.v-data-table__th:nth-child(1)),
:deep(.v-data-table__td:nth-child(1)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.v-data-table__th:nth-child(2)),
:deep(.v-data-table__td:nth-child(2)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.v-data-table__th:nth-child(3)),
:deep(.v-data-table__td:nth-child(3)) {
  width: 9rem !important;
  min-width: 9rem !important;
}

:deep(.v-data-table__th:nth-child(4)),
:deep(.v-data-table__td:nth-child(4)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.v-data-table__th:nth-child(5)),
:deep(.v-data-table__td:nth-child(5)) {
  width: 9rem !important;
  min-width: 9rem !important;
}

:deep(.v-data-table__th:nth-child(6)),
:deep(.v-data-table__td:nth-child(6)) {
  width: 10rem !important;
  min-width: 10rem !important;
}

/* Колонки с текстом контента («Текст обращения» и «Ответ») получают всё основное пространство поровну */
:deep(.v-data-table__th:nth-child(7)),
:deep(.v-data-table__td:nth-child(7)),
:deep(.v-data-table__th:nth-child(8)),
:deep(.v-data-table__td:nth-child(8)) {
  min-width: 20rem !important;
  width: 50% !important;
}

:deep(.v-data-table__th:nth-child(9)),
:deep(.v-data-table__td:nth-child(9)),
:deep(.v-data-table__th:nth-child(10)),
:deep(.v-data-table__td:nth-child(10)) {
  width: 8rem !important;
  min-width: 8rem !important;
}

:deep(.v-data-table__th:nth-child(11)),
:deep(.v-data-table__td:nth-child(11)),
:deep(.v-data-table__th:nth-child(12)),
:deep(.v-data-table__td:nth-child(12)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.v-data-table__th) {
  white-space: nowrap;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.status-badge.answered) {
  background-color: rgb(var(--v-theme-success), var(--v-activated-opacity)) !important;
  color: rgb(var(--v-theme-success)) !important;
}

:deep(.status-badge.new) {
  background-color: rgb(var(--v-theme-info), var(--v-activated-opacity)) !important;
  color: rgb(var(--v-theme-info)) !important;
}
</style>
